import { Link } from 'react-router-dom';
import { buttonClasses } from '@/components/ui/Button';
import { Icon, type IconName } from '@/components/ui/Icon';
import { ProductCard } from '@/components/product/ProductCard';
import { ProductGrid } from '@/components/product/ProductGrid';
import { ProductCardSkeleton } from '@/components/common/Skeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { useFeatured, useProducts } from '@/hooks/useProducts';
import { useDocumentMeta } from '@/lib/seo';
import styles from './Home.module.css';

const TRUST_POINTS: { icon: IconName; title: string; sub: string }[] = [
  { icon: 'bolt', title: 'Instant Delivery', sub: 'Most orders in minutes' },
  { icon: 'shield', title: 'Verified Seller', sub: '100% safe trades' },
  { icon: 'check', title: '24/7 Support', sub: 'We reply fast on Discord' },
  { icon: 'star', title: '4.9/5 Rating', sub: 'From thousands of buyers' },
];

export function Home() {
  const featured = useFeatured();
  const popular = useProducts({ sort: 'popularity', pageSize: 8, inStock: true });

  useDocumentMeta({
    title: 'Kon Store — Fast, Safe Grow a Garden Items',
    description:
      'Buy Grow a Garden seeds, pets, gear, and bundles with instant delivery. Verified seller, secure trades, thousands of completed orders.',
    canonicalPath: '/',
  });

  return (
    <>
      {/* Hero */}
      <section className={styles.hero}>
        <div className={styles.heroInner}>
          <span className={styles.eyebrow}>
            <Icon name="sparkles" size={14} /> Grow a Garden Marketplace
          </span>
          <h1 className={styles.title}>
            Fast, Safe Grow a Garden Items — <span>Instant Delivery</span>
          </h1>
          <p className={styles.sub}>
            Stock up on the rarest seeds, pets, and gear. Verified seller, secure trades, and
            lightning-fast delivery straight to your garden.
          </p>
          <div className={styles.ctas}>
            <Link to="/catalog" className={buttonClasses('primary', 'lg')}>
              Shop All Items
              <Icon name="chevron-right" size={18} />
            </Link>
            <Link to="/catalog?category=bundle" className={buttonClasses('secondary', 'lg')}>
              Browse Bundles
            </Link>
          </div>
          <div className={styles.stats}>
            <div className={styles.stat}>
              <span className={styles.statValue}>12,400+</span>
              <span className={styles.statLabel}>Orders delivered</span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statValue}>
                4.9<b>★</b>
              </span>
              <span className={styles.statLabel}>Average rating</span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statValue}>~3 min</span>
              <span className={styles.statLabel}>Avg. delivery time</span>
            </div>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <div className={styles.trustBar}>
        {TRUST_POINTS.map((point) => (
          <div key={point.title} className={styles.trust}>
            <span className={styles.trustIcon}>
              <Icon name={point.icon} size={20} />
            </span>
            <span className={styles.trustText}>
              <b>{point.title}</b>
              <span>{point.sub}</span>
            </span>
          </div>
        ))}
      </div>

      {/* Featured strip */}
      <section className={styles.section} aria-labelledby="featured-heading">
        <div className={styles.sectionHead}>
          <h2 id="featured-heading" className={styles.sectionTitle}>
            <Icon name="star" size={22} style={{ color: 'var(--color-gold)' }} /> Featured
          </h2>
          <Link to="/catalog?featured=true" className={styles.seeAll}>
            See all <Icon name="chevron-right" size={14} />
          </Link>
        </div>
        {featured.isLoading ? (
          <div className={styles.strip}>
            {Array.from({ length: 5 }, (_, i) => (
              <ProductCardSkeleton key={i} />
            ))}
          </div>
        ) : featured.data && featured.data.length > 0 ? (
          <div className={styles.strip}>
            {featured.data.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        ) : (
          <EmptyState glyph="✨" title="No featured items yet">
            Check back soon — our best deals get featured here.
          </EmptyState>
        )}
      </section>

      {/* Popular grid */}
      <section className={styles.section} aria-labelledby="popular-heading">
        <div className={styles.sectionHead}>
          <h2 id="popular-heading" className={styles.sectionTitle}>
            Popular Right Now
          </h2>
          <Link to="/catalog" className={styles.seeAll}>
            View catalog <Icon name="chevron-right" size={14} />
          </Link>
        </div>
        <ProductGrid
          products={popular.data?.items ?? []}
          loading={popular.isLoading}
          eagerCount={4}
          empty={<EmptyState glyph="🌱" title="Catalog is being stocked" />}
        />
      </section>
    </>
  );
}
